import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { authService, userService, goalService } from './services/api';
import './App.css';
import { useTheme, ThemeProvider } from './ThemeContext';
import AuthGuard from './components/AuthGuard';

// Import components
import AuthenticatedHeader from './components/AuthenticatedHeader';
import SideMenu from './components/SideMenu';
import Landing from './components/Landing';
import Login from './components/Login';
import Register from './components/Register';
import CreateGoal from './components/CreateGoal';
import KnowledgeAssessment from './components/KnowledgeAssessment';
import LearningPlan from './components/LearningPlan';
import KnowledgeGraph from './components/KnowledgeGraph';
import Lesson from './components/Lesson';
import LessonAssessment from './components/LessonAssessment';
import Settings from './components/Settings';
import PomodoroTimer from './components/PomodoroTimer';
import Home from './components/Home';
import GoalModal from './components/GoalModal';
import GoalsPage from './components/GoalsPage';
import WelcomeScreen from './components/WelcomeScreen';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionCancel from './components/SubscriptionCancel';
import InitialSetup from './components/InitialSetup';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import BlogPage from './components/BlogPage';
import BlogPost from './components/BlogPost';


// Protected route component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Wrap authenticated content with the authenticated-app class and AuthGuard
  return (
    <div className="authenticated-app">
      <AuthGuard>
        <div className="app-layout">
          <SideMenu />
          <div className="main-content">
            {children}
          </div>
        </div>
      </AuthGuard>
    </div>
  );
};

// Public route component to redirect logged in users to dashboard
const PublicRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  const location = useLocation();
  
  // If user is logged in and trying to access login or register, redirect to home
  if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/')) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// AppContent component to handle route-specific logic
const AppContent = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isInitialSetupPage = location.pathname === '/initial-setup';
  
  // Check if user is logged in and fetch user data
  useEffect(() => {
    const checkAuth = async () => {
      const authenticated = authService.isAuthenticated();
      setIsLoggedIn(authenticated);
      
      if (authenticated) {
        try {
          // Fetch user data
          const userData = await userService.getUser();
          setUser(userData);
          
          // Check if welcome screen has already been shown in this session
          const welcomeShown = sessionStorage.getItem('welcomeScreenShown');
          
          // Only proceed with showing welcome screen if it hasn't been shown yet
          if (!welcomeShown) {
            // Check if the user has any goals
            try {
              const userGoals = await goalService.getUserGoals();
              
              // Only show welcome screen if user has no goals at all
              if (!userGoals || userGoals.length === 0) {
                setShowWelcome(true); // Show welcome screen for new users
                // Mark that we've shown the welcome screen in this session
                sessionStorage.setItem('welcomeScreenShown', 'true');
              }
            } catch (error) {
              console.error('Error checking for user goals:', error);
              // If we can't check for goals, assume user needs welcome screen
              setShowWelcome(true);
              // Mark that we've shown the welcome screen in this session
              sessionStorage.setItem('welcomeScreenShown', 'true');
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
    
    // Listen for changes to local storage to update auth state
    window.addEventListener('storage', checkAuth);
    
    // Add a custom event listener for auth changes
    window.addEventListener('auth-change', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('auth-change', checkAuth);
    };
  }, []);

  // Listen for PRO gating events and show modal
  useEffect(() => {
    const handleProRequired = () => {
      setShowProModal(true);
    };
    window.addEventListener('pro-required', handleProRequired);
    return () => window.removeEventListener('pro-required', handleProRequired);
  }, []);

  const handleGetProClick = () => {
    setShowProModal(false);
    navigate('/settings', { state: { activeTab: 'subscription' } });
  };

  const handleCloseProModal = () => {
    setShowProModal(false);
  };
  
  // Global layout protection mechanism
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const ensureLayoutIntegrity = () => {
      const mainContent = document.querySelector('.app-layout .main-content');
      if (mainContent) {
        // Remove any problematic inline styles
        mainContent.style.marginLeft = '';
        mainContent.style.width = '';
        
        // Force reflow to ensure CSS rules are applied
        // eslint-disable-next-line no-unused-expressions
        mainContent.offsetHeight;
        
        // Ensure proper classes are present
        if (!mainContent.classList.contains('main-content')) {
          mainContent.classList.add('main-content');
        }
      }
    };
    
    // Run layout check on route changes
    ensureLayoutIntegrity();
    
    // Removed periodic layout check; a single call on route change is enough to keep layout aligned
    // const layoutCheckInterval = setInterval(ensureLayoutIntegrity, 1000);
    
    // Listen for route changes
    const handleRouteChange = () => {
      // Delay to allow component to mount
      setTimeout(ensureLayoutIntegrity, 100);
    };
    
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      // if (layoutCheckInterval) clearInterval(layoutCheckInterval);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [location.pathname, isLoggedIn]);

  // Redirect to initial setup if user hasn't completed it
  useEffect(() => {
    if (!isLoggedIn) return;
    if (user && user.isInitialSetupCompleted === false && location.pathname !== '/initial-setup') {
      navigate('/initial-setup', { replace: true });
    }
  }, [isLoggedIn, user, location.pathname, navigate]);
  
  // Handler for when welcome screen completes
  const handleWelcomeComplete = () => {
    setShowWelcome(false);
    setShowGoalModal(true);
    // Ensure we remember the welcome screen was shown in this session
    sessionStorage.setItem('welcomeScreenShown', 'true');
  };
  
  // Handler for when goal is set
  const handleGoalSet = async () => {
    try {
      // Refresh user data
      const refreshedUser = await userService.getUser();
      setUser(refreshedUser);
      setShowGoalModal(false);
    } catch (error) {
      console.error('Error refreshing user data after goal set:', error);
    }
  };
  
  // Handler for logout
  const handleLogout = () => {
    // Clear the welcome screen session storage flag
    sessionStorage.removeItem('welcomeScreenShown');
    // Perform logout (this will trigger the auth-change event)
    authService.logout();
  };
  
  // Check if current route is a blog page
  const isBlogPage = location.pathname.startsWith('/blog');
  
  // Only show public header on public routes (excluding landing page and blog pages)
  const showPublicHeader = !isLoggedIn && location.pathname !== '/' && !isBlogPage;
  
  // Show loading indicator while checking authentication
  if (isLoading) {
    return <div className="loading-app">Loading...</div>;
  }
  
  return (
    <>
      {isLoggedIn && location.pathname !== '/initial-setup' && !isBlogPage && (
        <AuthenticatedHeader user={user} onLogout={handleLogout} />
      )}
      
      {/* Show welcome screen before goal modal */}
      {isLoggedIn && showWelcome && user && (
        <WelcomeScreen onComplete={handleWelcomeComplete} />
      )}
      
      {/* Show goal modal after welcome screen */}
      {isLoggedIn && showGoalModal && user && (
        <GoalModal userId={user.id} onGoalSet={handleGoalSet} />
      )}
      
      <div className={`app-content ${showPublicHeader && !isBlogPage ? 'with-header' : ''} ${isLoggedIn && !isBlogPage ? 'with-authenticated-header' : ''} ${isInitialSetupPage ? 'initial-setup-page' : ''}`}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={
            <PublicRoute>
              <Landing />
            </PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          
          {/* Subscription routes */}
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancel />} />

          {/* Legal pages - public routes */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Blog routes - public routes */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPost />} />
        
          {/* Home route - default for authenticated users */}
          <Route path="/home" element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          } />

          {/* Initial setup route - protected but without side menu/header */}
          <Route path="/initial-setup" element={
            authService.isAuthenticated() ? (
              <div className="initial-setup-container">
                <InitialSetup />
              </div>
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          
          {/* Remove Goals Page Route */}
          <Route path="/goals" element={
            <ProtectedRoute>
              <GoalsPage />
            </ProtectedRoute>
          } />
          
          {/* Restore old /goals/new route if CreateGoal component exists and is used */}
          <Route path="/goals/new" element={
            <ProtectedRoute>
              <CreateGoal />
            </ProtectedRoute>
          } />
          
          <Route path="/goals/:goalId/assessment" element={
            <ProtectedRoute>
              <KnowledgeAssessment />
            </ProtectedRoute>
          } />
          
          {/* Learning Plan routes */}
          <Route path="/learning-plan" element={
            <ProtectedRoute>
              <LearningPlan />
            </ProtectedRoute>
          } />
          
          <Route path="/learning-plan/:courseId" element={
            <ProtectedRoute>
              <LearningPlan />
            </ProtectedRoute>
          } />
          
          {/* Knowledge Graph route - temporarily disabled */}
          <Route path="/knowledge-graph" element={
            <ProtectedRoute>
              <KnowledgeGraph />
            </ProtectedRoute>
          } />

          {/* Settings route */}
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          
          <Route path="/lessons/:lessonId" element={
            <ProtectedRoute>
              <Lesson />
            </ProtectedRoute>
          } />
          
          <Route path="/lessons/:lessonId/assessment" element={
            <ProtectedRoute>
              <LessonAssessment />
            </ProtectedRoute>
          } />
          
          {/* Redirect to home page by default for authenticated users, landing for others */}
          <Route path="*" element={
            isLoggedIn ? <Navigate to="/home" replace /> : <Navigate to="/" replace />
          } />
        </Routes>
        
        {/* Show Pomodoro Timer on all authenticated pages except initial setup and blog */}
        {isLoggedIn && location.pathname !== '/initial-setup' && !isBlogPage && <PomodoroTimer />}

        {/* Pro Required Modal */}
        {showProModal && (
          
      <div className="roadmap-modal show">
      <div className={`modal-content ${theme}`}>
        <div className="modal-header">
          <h3 className="modal-title">Free Plan Limit Reached</h3>
          <button 
            className="close-button" 
            onClick={handleCloseProModal}
          >
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="lesson-limit-warning">
            <div className="limit-icon">⚠️</div>
            <div className="limit-message">
              <p>You've completed 1 topic in this course. Free plan users can only complete 1 topic per course.</p>
            </div>
            <div className="limit-suggestion">
              <p>To continue learning and unlock unlimited topics, upgrade to Pro and get:</p>
            </div>
            <div className="limit-features">
              <h4>Pro Features:</h4>
              <ul>
                <li>✨ Up to 10 goals in progress</li>
                <li>💬 Unlimited AI Chat Queries</li>
                <li>📚 Unlimited AI Lessons</li>
                <li>📝 Unlimited AI Assessments</li>
                <li>🤖 Best AI Models</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button 
            className="cancel-button" 
            onClick={handleCloseProModal}
          >
            Maybe Later
          </button>
          <button 
            className="confirm-button upgrade" 
            onClick={handleGetProClick}
          >
            Upgrade to Pro
          </button>
        </div>
      </div>
    </div>

        )}
      </div>
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
